// The single most recent weather alert for this screen's city, held on screen
// for as long as it is current.
//
// This replaces the popup behaviour of useAlertPopup: that hook showed each
// alert exactly once for 30 s (tracked in localStorage) and then dropped it, so
// anyone who wasn't looking at the screen in that window never saw it. Here the
// newest alert simply stays in its own panel until either a newer one arrives
// and replaces it, or it ages past the 24-hour window and the panel empties.
//
// The 24-hour cutoff is enforced by the backend (WEATHER_ALERTS_MAX_AGE_HOURS,
// applied in alertService before the list is sorted newest-first), so the head
// of the response is exactly the alert to show. It is re-checked here as well:
// a display that loses the backend for hours must not keep yesterday's warning
// up indefinitely.

import { useState, useEffect } from 'react';
import { fetchScreenAlerts } from '../services/alertsApi';
import { hasScreenCredentials } from '../../../config';

const POLL_MS = 60 * 1000;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function isCurrent(alert) {
  if (!alert) return false;
  const createdAt = Date.parse(alert.createdAt);
  // An unparseable timestamp still passed the backend's own age filter, so
  // trust it rather than hiding a real warning.
  if (!Number.isFinite(createdAt)) return true;
  return Date.now() - createdAt <= MAX_AGE_MS;
}

export function useLatestAlert() {
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!hasScreenCredentials()) return undefined;

    let cancelled = false;

    async function poll() {
      try {
        const { alerts } = await fetchScreenAlerts();
        if (cancelled) return;

        const newest = Array.isArray(alerts) && alerts.length ? alerts[0] : null;
        setAlert(isCurrent(newest) ? newest : null);
      } catch {
        // Backend unreachable: keep the alert already on screen, but still let
        // it age out on its own timestamp.
        if (!cancelled) {
          setAlert((current) => (isCurrent(current) ? current : null));
        }
      }
    }

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return alert;
}
